import { useMemo } from "react";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseBloodPressureChartConfigProps {
  selectedRange: TimeRange;
  navigationOffset: number;
  selectedDate: Date;
  systolicChartData: ChartDataPoint[];
  diastolicChartData: ChartDataPoint[];
}

interface ChartConfig {
  scale: { x: "time" | "linear" };
  domain: {
    x: [Date, Date] | [number, number];
    y: [number, number];
  };
  tickValues: (Date | number)[];

  yTickValues?: number[]; // Add Y-axis tick values
}

export const useBloodPressureChartConfig = ({
  selectedRange,
  navigationOffset: _navigationOffset,
  selectedDate,
  systolicChartData,
  diastolicChartData,
}: UseBloodPressureChartConfigProps): ChartConfig => {
  const chartConfig = useMemo(() => {
    const today = new Date();

    // Calculate domain based on actual data if available
    let dataDomain = null;
    let dataYear = today.getFullYear(); // Default to current year

    // Calculate dynamic Y domain based on actual blood pressure data
    let yDomain: [number, number] = [30, 180]; // Reasonable blood pressure range (mmHg) - includes low diastolic values
    let yTickValues: number[] = [30, 50, 70, 90, 110, 130, 150, 170]; // Reasonable blood pressure tick values

    if (systolicChartData.length > 0) {
      // Combine both systolic and diastolic data to get the full range
      const allYValues = [
        ...systolicChartData.map((point) => point.y),
        ...diastolicChartData.map((point) => point.y),
      ];
      const minY = Math.min(...allYValues);
      const maxY = Math.max(...allYValues);

      // For blood pressure, ensure reasonable range that adapts to data
      if (allYValues.length === 1) {
        // Single data point: create range around it
        const value = allYValues[0];
        const range = 40; // 40 mmHg range around the value
        yDomain = [
          Math.max(30, Math.floor(value - range / 2)), // Minimum 30 mmHg to include low diastolic values
          Math.min(220, Math.ceil(value + range / 2)), // Maximum 220 mmHg
        ];
      } else {
        // Multiple data points: add more generous padding to ensure all data is visible
        const dataRange = maxY - minY;
        const yPadding = Math.max(20, dataRange * 0.25); // At least 20 mmHg padding, or 25% of data range
        yDomain = [
          Math.max(30, Math.floor(minY - yPadding)), // Minimum 30 mmHg to include low diastolic values
          Math.min(220, Math.ceil(maxY + yPadding)), // Maximum 220 mmHg
        ];
      }

      // Ensure Y-axis domain is consistent across all time ranges
      // Force minimum range of 40 mmHg to prevent tiny ranges and ensure visibility
      const currentRange = yDomain[1] - yDomain[0];
      if (currentRange < 40) {
        const center = (yDomain[0] + yDomain[1]) / 2;
        yDomain = [
          Math.max(30, Math.floor(center - 20)), // Minimum 30 mmHg to include low diastolic values
          Math.min(220, Math.ceil(center + 20)),
        ];
      }

      // Generate appropriate tick values based on the domain
      const range = yDomain[1] - yDomain[0];
      const tickCount = 6; // Number of ticks we want
      const tickStep = range / (tickCount - 1);
      yTickValues = Array.from({ length: tickCount }, (_, i) =>
        Math.round(yDomain[0] + i * tickStep),
      );
    }

    // Safely handle xValues calculation
    if (systolicChartData.length > 0) {
      const xValues = systolicChartData.map((point) => point.x);
      const xTimes = xValues.map((x) =>
        typeof x === "number" ? x : x.getTime(),
      );

      // Check for valid numbers
      const validTimes = xTimes.filter(
        (time) => !isNaN(time) && isFinite(time),
      );

      if (validTimes.length > 0) {
        const minX = Math.min(...validTimes);
        const maxX = Math.max(...validTimes);
        dataDomain = { min: minX, max: maxX };
      }
    }

    // Get the year from the actual data (using local timezone)
    if (dataDomain) {
      const firstDataPoint = new Date(dataDomain.min);
      dataYear = firstDataPoint.getFullYear();
    }

    switch (selectedRange) {
      case "1d": {
        // Show every 2 hours: 12am, 2am, 4am, 6am, 8am, 10am, 12pm, 2pm, 4pm, 6pm, 8pm, 10pm
        // Use local timezone for day view

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
          yTickValues,
        };
      }
      case "7d": {
        // Use selectedDate if provided, otherwise fall back to current date + navigation offset
        const baseDate = selectedDate ?? new Date();
        const dayOfWeek = baseDate.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

        const weekStart = new Date(baseDate);
        weekStart.setDate(baseDate.getDate() - daysToMonday);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);
        weekEnd.setHours(0, 0, 0, 0);

        // Generate 7 ticks for the week (Monday to Sunday)
        const weekTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i);
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [weekStart, weekEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: weekTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1m": {
        // Current month view
        const currentYear = selectedDate?.getFullYear() ?? today.getFullYear();
        const currentMonth = selectedDate?.getMonth() ?? today.getMonth();

        const monthStart = new Date(currentYear, currentMonth, 1);
        const monthEnd = new Date(currentYear, currentMonth + 1, 1);

        // Generate ticks for each week in the month
        const monthTicks = [];
        const current = new Date(monthStart);
        while (current < monthEnd) {
          monthTicks.push(new Date(current));
          current.setDate(current.getDate() + 7);
        }

        return {
          scale: { x: "time" as const },
          domain: {
            x: [monthStart, monthEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: monthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { day: "numeric" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "6m": {
        // Current 6-month period view
        const currentYear = selectedDate?.getFullYear() ?? today.getFullYear();
        const currentMonth = selectedDate?.getMonth() ?? today.getMonth();
        const isFirstHalf = currentMonth < 6;

        const periodStart = new Date(currentYear, isFirstHalf ? 0 : 6, 1);
        const periodEnd = new Date(currentYear, isFirstHalf ? 6 : 12, 1);

        // Generate monthly ticks
        const monthTicks = [];
        const current = new Date(periodStart);
        while (current < periodEnd) {
          monthTicks.push(new Date(current));
          current.setMonth(current.getMonth() + 1);
        }

        return {
          scale: { x: "time" as const },
          domain: {
            x: [periodStart, periodEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: monthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1y": {
        // Current year view
        const targetYear = dataYear;

        const yearTicks = [
          new Date(targetYear, 0, 1, 0, 0, 0), // Jan
          new Date(targetYear, 1, 1, 0, 0, 0), // Feb
          new Date(targetYear, 2, 1, 0, 0, 0), // Mar
          new Date(targetYear, 3, 1, 0, 0, 0), // Apr
          new Date(targetYear, 4, 1, 0, 0, 0), // May
          new Date(targetYear, 5, 1, 0, 0, 0), // Jun
          new Date(targetYear, 6, 1, 0, 0, 0), // Jul
          new Date(targetYear, 7, 1, 0, 0, 0), // Aug
          new Date(targetYear, 8, 1, 0, 0, 0), // Sep
          new Date(targetYear, 9, 1, 0, 0, 0), // Oct
          new Date(targetYear, 10, 1, 0, 0, 0), // Nov
          new Date(targetYear, 11, 1, 0, 0, 0), // Dec
        ];

        return {
          scale: { x: "time" as const },
          domain: {
            x: [new Date(targetYear, 0, 1), new Date(targetYear, 11, 1)] as [
              Date,
              Date,
            ],
            y: yDomain,
          },
          tickValues: yearTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      default: {
        // Default to week view - always use predefined ticks
        const defaultTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(today);
          date.setDate(date.getDate() - (6 - i));
          return date;
        });
        return {
          scale: { x: "time" as const },
          domain: dataDomain
            ? {
                x: [new Date(dataDomain.min), new Date(dataDomain.max)] as [
                  Date,
                  Date,
                ],
                y: yDomain,
              }
            : { y: yDomain },
          tickValues: defaultTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
    }
  }, [selectedRange, selectedDate, systolicChartData, diastolicChartData]);

  // Ensure domain.x is always defined to satisfy ChartConfig type
  if (!chartConfig.domain.x) {
    const now = new Date();
    chartConfig.domain = {
      ...chartConfig.domain,
      x: [now, now] as [Date, Date],
    };
  }

  // Final consistency check - ensure Y domain is reasonable
  if (chartConfig.domain.y[1] - chartConfig.domain.y[0] < 20) {
    const center = (chartConfig.domain.y[0] + chartConfig.domain.y[1]) / 2;
    chartConfig.domain.y = [
      Math.max(30, center - 10), // Minimum 30 mmHg to include low diastolic values
      Math.min(220, center + 10),
    ];
  }

  return chartConfig;
};
